
import React, { useState, useCallback, useEffect } from "react";
import { Helmet } from "react-helmet";
import Layout from "@/components/layout/Layout";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthProvider";
import { NursingHomeLeads } from "@/components/dashboard/NursingHomeLeads";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { ConsultationBanner } from "@/components/dashboard/ConsultationBanner";
import { OnboardingChecklist } from "@/components/dashboard/OnboardingChecklist";
import { OnboardingWizard, OnboardingData } from "@/components/onboarding/OnboardingWizard";
import { useDashboardData } from "@/hooks/use-dashboard-data";
import { useGuestSavesMigration } from "@/hooks/use-guest-saves-migration";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, AlertCircle, Mail, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, isLoading: authLoading } = useAuth();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { nursingHomeLeads, waitlistData, isLoading, error, refetch } = useDashboardData(user, refreshTrigger);
  const isMobile = useIsMobile();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [hasPreferences, setHasPreferences] = useState(false);
  const [profileChecked, setProfileChecked] = useState(false);
  const [showVerifyBanner, setShowVerifyBanner] = useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  
  // Auto-migrate guest saves to leads when user signs in
  useGuestSavesMigration();

  // Function to trigger data refresh
  const handleLeadUpdate = useCallback(() => {
    console.log("Updating leads...");
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Check if user has completed onboarding
  useEffect(() => {
    const checkProfile = async () => {
      if (!user) return;
      
      const { data: profile } = await supabase
        .from("profiles")
        .select("saved_preferences")
        .eq("id", user.id)
        .maybeSingle();

      const hasPrefs = !!profile?.saved_preferences;
      setHasPreferences(hasPrefs);
      
      // Show onboarding wizard if user has no preferences AND just signed up
      const justCompleted = sessionStorage.getItem("justCompletedAuth");
      const justVerified = sessionStorage.getItem("justVerified");
      
      if (!hasPrefs && (justCompleted || justVerified)) {
        setShowOnboarding(true);
        sessionStorage.removeItem("justCompletedAuth");
        sessionStorage.removeItem("justVerified");
      } else {
        if (justVerified) {
          sessionStorage.removeItem("justVerified");
          handleLeadUpdate();
        }
        if (justCompleted) {
          sessionStorage.removeItem("justCompletedAuth");
        }
      }
      
      // Show verify banner if email not confirmed
      const emailConfirmed = user.email_confirmed_at || user.confirmed_at;
      setShowVerifyBanner(!emailConfirmed);
      
      setProfileChecked(true);
    };

    if (!authLoading && user) {
      checkProfile();
    }
  }, [user, authLoading, handleLeadUpdate]);

  const handleOnboardingComplete = async (data: OnboardingData) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          saved_preferences: {
            careNeeds: data.careNeeds,
            timeframe: data.timeframe,
            counties: data.counties,
            priorities: data.priorities,
            onboarding_completed_at: new Date().toISOString(),
          },
        })
        .eq("id", user.id);

      if (error) throw error;

      setHasPreferences(true);
      setShowOnboarding(false);
      toast.success("Preferences saved!", {
        description: "We'll show you personalised recommendations.",
      });
    } catch (err) {
      console.error("Error saving onboarding data:", err);
      toast.error("Couldn't save preferences. Please try again.");
    }
  };

  const handleOnboardingSkip = () => {
    setShowOnboarding(false);
    sessionStorage.removeItem("justCompletedAuth");
    sessionStorage.removeItem("justVerified");
  };

  const handleResendVerification = async () => {
    if (!user?.email) return;
    setIsResendingVerification(true);
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email: user.email });
      if (error) throw error;
      toast.success("Verification email sent!", { description: "Check your inbox or spam folder." });
    } catch (err: any) {
      toast.error("Failed to send email", { description: err.message });
    } finally {
      setIsResendingVerification(false);
    }
  };

  // Redirect to login if not authenticated
  if (!authLoading && !user) {
    return <Navigate to="/auth/login?redirectTo=/dashboard" />;
  }

  // Show onboarding wizard
  if (showOnboarding && user) {
    return (
      <Layout>
        <Helmet>
          <title>Welcome to Grandpal | Set Up Your Preferences</title>
        </Helmet>
        <div className="container py-8 md:py-16 max-w-7xl px-3 md:px-4">
          <OnboardingWizard
            firstName={user.user_metadata?.first_name}
            onComplete={handleOnboardingComplete}
            onSkip={handleOnboardingSkip}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>My Dashboard | Grandpal</title>
      </Helmet>

      <div className="container py-4 md:py-8 max-w-7xl px-3 md:px-4">
        <DashboardHeader user={user} />

        {/* Email verification banner */}
        {showVerifyBanner && (
          <div className="relative flex items-start gap-3 p-4 rounded-lg border border-amber-200 bg-amber-50 mt-4">
            <Mail className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900">Please verify your email</p>
              <p className="text-sm text-amber-700 mt-0.5">
                Check your inbox for a verification link to keep your account secure.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2 border-amber-300 text-amber-800 hover:bg-amber-100"
                onClick={handleResendVerification}
                disabled={isResendingVerification}
              >
                {isResendingVerification ? "Sending..." : "Resend verification email"}
              </Button>
            </div>
            <button onClick={() => setShowVerifyBanner(false)} className="text-amber-400 hover:text-amber-600">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        
        {isLoading || authLoading || !profileChecked ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center">
              <Loader2 className="h-10 w-10 text-grandpal-green animate-spin mb-4" />
              <p className="text-gray-600">Loading your dashboard...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="h-10 w-10 text-red-500 mb-4" />
            <h3 className="text-lg font-medium mb-2">Error Loading Dashboard</h3>
            <p className="text-gray-600 mb-4">We encountered an issue while loading your dashboard data.</p>
            <button 
              onClick={refetch}
              className="px-4 py-2 bg-grandpal-green text-white rounded-md hover:bg-grandpal-green-dark transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="space-y-6 mt-4">
            {/* Onboarding checklist for new users */}
            <OnboardingChecklist
              hasPreferences={hasPreferences}
              hasLeads={nursingHomeLeads.length > 0}
              hasCareRecipient={false}
              hasWaitlistSubmission={!!waitlistData}
            />
            
            <ConsultationBanner />
            
            <NursingHomeLeads 
              leads={nursingHomeLeads} 
              onLeadUpdate={handleLeadUpdate} 
            />
          </div>
        )}
      </div>
    </Layout>
  );
}
